(() => {
  'use strict';

  // EVERY WIDTH SINCE 2026-10-29 (the owner: "the three icons on the right -
  // chat, rewards, Google - like on the mobile version ... and the chat icon
  // must not jump"). On the desktop Inbox drew its own dark "Chat" pill, and it
  // jumped: measured at 824 and 1440, it first stood at right 35 / bottom 88
  // and was redrawn elsewhere once Inbox's runtime loaded. Our button is placed
  // once by the stylesheet and never moves, so the desktop now gets the
  // phone's button and the phone's hide. The name is kept; it no longer narrows.
  const MOBILE_QUERY = window.matchMedia('all');
  const CHAT_HOST_SELECTOR = 'shopify-chat';
  const NATIVE_ACTIVATOR_SELECTOR = '.activator';
  const PROXY_SELECTOR = '[data-bc-mobile-chat-launcher]';
  const HIDE_STYLE_ID = 'bc-hide-native-shopify-chat-activator';
  const MAX_SHADOW_RETRIES = 20;
  let updateTimer = null;
  let shadowRetryTimer = null;
  let shadowRetryCount = 0;

  function findNativeChatHost() {
    const directHost = document.querySelector(CHAT_HOST_SELECTOR);

    if (directHost?.shadowRoot) {
      return directHost;
    }

    for (const element of document.querySelectorAll('*')) {
      if (
        element.shadowRoot?.querySelector(NATIVE_ACTIVATOR_SELECTOR) ||
        element.shadowRoot?.querySelector('button[aria-label*="chat" i]')
      ) {
        return element;
      }
    }

    return directHost;
  }

  function findNativeChatActivator() {
    const host = findNativeChatHost();

    if (!host?.shadowRoot) return null;

    return (
      host.shadowRoot.querySelector(NATIVE_ACTIVATOR_SELECTOR) ||
      host.shadowRoot.querySelector('button[aria-label*="chat" i], button')
    );
  }

  function getHideStyle(host) {
    return host?.shadowRoot?.getElementById(HIDE_STYLE_ID) || null;
  }

  const HIDE_CSS = `
      .activator-nudge,
      ${NATIVE_ACTIVATOR_SELECTOR} {
        position: fixed !important;
        top: 0 !important;
        left: -10000px !important;
        right: auto !important;
        bottom: auto !important;
        width: 1px !important;
        height: 1px !important;
        min-width: 1px !important;
        min-height: 1px !important;
        max-width: 1px !important;
        max-height: 1px !important;
        margin: 0 !important;
        padding: 0 !important;
        border: 0 !important;
        opacity: 0 !important;
        pointer-events: none !important;
        overflow: hidden !important;
        transform: none !important;
      }
    `;

  // Two chat buttons on a phone (2026-09-13): the hide style lived only as a
  // <style> child of Inbox's shadow root, put there once. When Inbox redraws
  // its root the child goes, the document observer below cannot see inside a
  // shadow root, and the retries stop after 5s - so Inbox's own "Chat" pill
  // came back beside ours. The rule is now also an adopted stylesheet, which
  // a redraw of the root's children does not remove, and the shadow root has
  // an observer of its own that puts the <style> back if it goes.
  let hideSheet = null;
  const watchedRoots = new WeakSet();

  function getHideSheet() {
    if (hideSheet) return hideSheet;

    try {
      hideSheet = new CSSStyleSheet();
      hideSheet.replaceSync(HIDE_CSS);
    } catch (error) {
      hideSheet = null;
    }

    return hideSheet;
  }

  function watchShadowRoot(root) {
    if (watchedRoots.has(root)) return;

    watchedRoots.add(root);
    new MutationObserver(scheduleUpdate).observe(root, { childList: true });
  }

  function hideNativeMobileActivator(host) {
    if (!host?.shadowRoot) return false;

    const root = host.shadowRoot;
    let style = getHideStyle(host);

    if (!style) {
      style = document.createElement('style');
      style.id = HIDE_STYLE_ID;
      root.appendChild(style);
    }

    // Written only when it differs: rewriting it is a mutation the shadow
    // root's observer would answer, every 50ms, for ever.
    if (style.textContent !== HIDE_CSS) {
      style.textContent = HIDE_CSS;
    }

    const sheet = getHideSheet();

    if (sheet && root.adoptedStyleSheets && !root.adoptedStyleSheets.includes(sheet)) {
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
    }

    watchShadowRoot(root);

    return Boolean(findNativeChatActivator());
  }

  function restoreNativeDesktopActivator() {
    const host = findNativeChatHost();
    getHideStyle(host)?.remove();

    if (hideSheet && host?.shadowRoot?.adoptedStyleSheets) {
      host.shadowRoot.adoptedStyleSheets = host.shadowRoot.adoptedStyleSheets.filter(
        (sheet) => sheet !== hideSheet
      );
    }
  }

  function createProxyButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'bc-mobile-chat-launcher';
    button.setAttribute('aria-label', 'Open chat');
    button.setAttribute('data-bc-mobile-chat-launcher', '');
    button.innerHTML = `
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        focusable="false"
      >
        <path
          d="M5 4.75h14A2.25 2.25 0 0 1 21.25 7v8A2.25 2.25 0 0 1 19 17.25h-7.1l-4.65 3.2v-3.2H5A2.25 2.25 0 0 1 2.75 15V7A2.25 2.25 0 0 1 5 4.75Z"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linejoin="round"
        />
      </svg>
    `;

    // THE PANEL IS OPENED THROUGH THE ELEMENT, NOT THROUGH ITS BUTTON
    // (2026-10-01). Inbox replaces the node it renders the first time the
    // chat opens - div.activator-nudge becomes shopify-agent-runtime - so a
    // handler that reaches for .activator works once and then finds nothing.
    // <shopify-chat> is a custom element with show(), close() and an observed
    // [open] attribute, and that attribute is also what the stylesheet keys
    // the hide on, so opening it here and hiding it there can never disagree.
    // The old click is kept last, for a host that is not this element.
    button.addEventListener('click', () => {
      const host = findNativeChatHost();

      if (host) {
        if (typeof host.show === 'function') {
          host.show();
          return;
        }

        if (typeof host.open === 'function') {
          host.open();
          return;
        }

        if (host.tagName === 'SHOPIFY-CHAT') {
          host.setAttribute('open', '');
          return;
        }
      }

      const activator = findNativeChatActivator();

      if (!activator) {
        console.warn(
          '[Billiard City Chat] Native Shopify Chat host not found.'
        );
        return;
      }

      activator.click();
    });

    return button;
  }

  function ensureSingleProxy() {
    const proxies = [...document.querySelectorAll(PROXY_SELECTOR)];
    const proxy = proxies.shift() || createProxyButton();

    proxies.forEach((duplicate) => duplicate.remove());

    if (!proxy.isConnected) {
      document.body.appendChild(proxy);
    }

    return proxy;
  }

  function clearShadowRetry() {
    if (shadowRetryTimer) {
      window.clearTimeout(shadowRetryTimer);
      shadowRetryTimer = null;
    }
  }

  function retryShadowSetup() {
    clearShadowRetry();

    if (
      !MOBILE_QUERY.matches ||
      shadowRetryCount >= MAX_SHADOW_RETRIES
    ) {
      return;
    }

    shadowRetryCount += 1;
    shadowRetryTimer = window.setTimeout(updateMobileChat, 250);
  }

  // THE END OF THE PAGE (2026-09-14, narrowed 2026-10-27). While the footer's
  // last row - payment icons and copyright - is on screen or within the
  // floaters' reach of it, html carries bc-at-page-end.
  //
  // WHAT THAT CLASS DOES IS NOW ONE SELECTOR: the closed MY OFFERS bar steps
  // aside (assets/bc-responsive.css, THE ROUND ICONS NEVER HIDE). The
  // launcher and the Google badge used to go with it; the owner's decision
  // of 2026-10-27 is that the round icons never disappear, at any scroll
  // position, on any width - so they stay and the footer's last row is
  // covered by them at the bottom of a phone page, measured and reported.
  //
  // The class is still set at every width, not only on the phone, because MY
  // OFFERS lies over the payment icons on the desktop too.
  //
  // The bottom root margin is the height the tallest floater stack reaches
  // above the viewport's bottom edge - on the phone the badge above the
  // launcher, 12 + 54 + 10 + 68 = 144; at 1280 the badge alone, 56 + 84 =
  // 140. With it the class is on whenever footer text under a floater could
  // be, not only once the copyright itself is in view.
  const PAGE_END_CLASS = 'bc-at-page-end';
  const FLOATER_REACH = 144;
  let pageEndObserver = null;
  let pageEndTarget = null;

  function watchPageEnd() {
    const root = document.documentElement;
    const target = document.querySelector('.footer-baseline');

    if (!target || !('IntersectionObserver' in window)) {
      if (pageEndObserver) { pageEndObserver.disconnect(); pageEndObserver = null; pageEndTarget = null; }
      root.classList.remove(PAGE_END_CLASS);
      return;
    }

    if (pageEndTarget === target) return;
    if (pageEndObserver) pageEndObserver.disconnect();
    pageEndTarget = target;
    pageEndObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      root.classList.toggle(PAGE_END_CLASS, entry.isIntersecting);
    }, { rootMargin: `0px 0px ${FLOATER_REACH}px 0px` });
    pageEndObserver.observe(target);
  }

  function updateMobileChat() {
    updateTimer = null;
    watchPageEnd();

    if (!MOBILE_QUERY.matches) {
      document.querySelectorAll(PROXY_SELECTOR).forEach((proxy) => {
        proxy.remove();
      });
      restoreNativeDesktopActivator();
      clearShadowRetry();
      shadowRetryCount = 0;
      return;
    }

    ensureSingleProxy();

    const host = findNativeChatHost();
    const isReady = hideNativeMobileActivator(host);

    if (isReady) {
      clearShadowRetry();
      shadowRetryCount = 0;
    } else {
      retryShadowSetup();
    }
  }

  function scheduleUpdate() {
    if (updateTimer) return;

    updateTimer = window.setTimeout(updateMobileChat, 50);
  }

  const observer = new MutationObserver(scheduleUpdate);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  if (MOBILE_QUERY.addEventListener) {
    MOBILE_QUERY.addEventListener('change', scheduleUpdate);
  } else {
    MOBILE_QUERY.addListener(scheduleUpdate);
  }

  updateMobileChat();
})();
